import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type {
  CompanyNameSlug as CompanyNameSlugFromGraph,
  CompanyProfile as CompanyProfileFromGraph,
  GroupedCompanies,
  PostCompanyProfileReviewFlagPayload as ReviewFlagPayloadFromGraph,
  CompanyReviewsV2 as ReviewsFromGraph,
  PostCompanyProfileReviewUpvotesPayload as ReviewUpvotesPayloadFromGraph,
  SearchCompanyDetails,
} from '@seek/ca-graphql-schema/types';
import {
  DEFAULT_LOGO_URL,
  MAXIMUM_CHARACTERS_SEARCH,
  MINIMUM_CHARACTERS_SEARCH,
  type Jobs,
  type Logger,
  type Reviews,
  type ReviewUpvotes as ReviewUpvotesFromGraph,
  type SortOrder,
  type Zone,
} from '@seek/libs-shared';

import type { CompaniesListModel } from '../../../companyProfiles/models/companiesListModel';
import type { AppConfig } from '../../config';
import { createJobService } from '../jobService/jobService';

import {
  GET_COMPANY_NAME_SLUG_QUERY,
  GET_COMPANY_PROFILES_BY_NAME_QUERY,
  GET_COMPANY_REVIEW_UPVOTES_QUERY,
  GET_GROUPED_COMPANIES_QUERY,
  GET_REVIEWS_QUERY,
  GET_SEARCH_COMPANY_PROFILES_QUERY,
  POST_COMPANY_REVIEW_FLAG_MUTATE,
  POST_COMPANY_REVIEW_UPVOTE_MUTATE,
} from './graphqlQueries';
import {
  mapCompanyGroupsToFlatCompanies,
  mapJobsModel,
  mapReviewsModel,
  mapSearchCompanyApiResultToSearchCompanyResult,
} from './mapper';
import type {
  SearchCompanyProfileApiResult,
  SearchCompanyProfileResult,
  SearchResultItem,
  SearchResults,
} from './types';

export type CompanyProfileService = ReturnType<
  typeof createCompanyProfileService
>;

export const createCompanyProfileService = ({
  config,
  client,
  logger,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  config: AppConfig;
  logger: Logger;
}) => {
  const REVIEWS_PER_PAGE = 30;

  const jobService = createJobService({
    config,
    logger,
  });

  const getJobs = async (organisationId?: string, page = 1): Promise<Jobs> => {
    if (!organisationId) {
      return { jobs: [], totalJobs: 0 };
    }

    const jobs = await jobService.getJobsList({
      organisationId,
      page,
      country: config.country,
      locale: config.locale,
    });

    return mapJobsModel(jobs);
  };

  const getReviews = async ({
    companyId,
    page = 1,
    sortOrder = 'most recent',
    perPage = REVIEWS_PER_PAGE,
    /**
     *! IMPORTANT NOTE:
     *! locationId is used to filter reviews, ONLY PASS IT when you are in the location based review route.
     *! USE `getLocationId()` helper function to determine if you are in the location based review route.
     *  */
    locationId,
  }: {
    companyId: string;
    locationId?: number;
    page?: number;
    perPage?: number;
    sortOrder?: SortOrder;
  }): Promise<Reviews> => {
    const variables = {
      companyProfileId: companyId,
      newCompanyProfileId: companyId,
      zone: config.zone,
      locale: config.locale,
      page,
      perPage,
      sort: sortOrder === 'most helpful' ? '-upvote_count' : '',
      locationId,
    };
    try {
      const response = await client.query<{
        companyDetails: { companyReviews: ReviewsFromGraph };
        companyProfile: CompanyProfileFromGraph;
      }>({
        query: GET_REVIEWS_QUERY,
        variables,
      });

      if (response?.error || response?.errors) {
        logger.error(
          { error: response.error ?? response.errors, variables },
          'Error calling getReviews',
        );
        return {
          companyId,
          name: '',
        };
      }

      return mapReviewsModel(
        response.data.companyProfile,
        response.data.companyDetails.companyReviews,
      );
    } catch (error) {
      logger.error({ error, variables }, 'Error from getReviews');
      return {
        companyId,
        name: '',
      };
    }
  };

  const getCompanyNameSlug = async (
    companySlug: string,
    isLegacyUrl: boolean,
  ): Promise<CompanyNameSlugFromGraph | undefined> => {
    const variables = {
      companySlug,
      zone: config.zone,
      isLegacyUrl,
    };
    const response = await client.query<{
      companyNameSlug: CompanyNameSlugFromGraph | null;
    }>({
      query: GET_COMPANY_NAME_SLUG_QUERY,
      variables,
    });

    if (response?.error || response?.errors) {
      logger.error(
        { error: response.error ?? response.errors, variables },
        'Error calling getCompanyNameSlug',
      );
    }

    return response.data.companyNameSlug ?? undefined;
  };

  const getUpvoteIds = async ({
    companyId,
  }: {
    companyId: string;
  }): Promise<ReviewUpvotesFromGraph> => {
    const variables = {
      companyProfileReviewUpvotesId: companyId,
      zone: config.zone,
    };
    const response = await client.query<{
      companyProfileReviewUpvotes: ReviewUpvotesFromGraph;
    }>({
      query: GET_COMPANY_REVIEW_UPVOTES_QUERY,
      variables,
    });

    if (response?.error || response?.errors) {
      logger.error(
        { error: response.error ?? response.errors, variables },
        'Error calling getUpvoteIds',
      );
    }

    return response?.data?.companyProfileReviewUpvotes ?? { upvotes: [] };
  };

  const postUpvoteReview = async ({
    companyId,
    reviewId,
  }: {
    companyId: string;
    reviewId: string;
  }): Promise<ReviewUpvotesPayloadFromGraph | undefined> => {
    const variables = {
      input: {
        companyId,
        reviewId,
      },
    };
    const response = await client.mutate<{
      postCompanyProfileReviewUpvotes: ReviewUpvotesPayloadFromGraph;
    }>({
      mutation: POST_COMPANY_REVIEW_UPVOTE_MUTATE,
      variables,
    });

    if (response?.errors) {
      logger.error(
        { error: response.errors, variables },
        'Error calling postUpvoteReview',
      );
    }

    return response?.data?.postCompanyProfileReviewUpvotes;
  };

  const postFlagReview = async ({
    companyId,
    reviewId,
    reason,
    details,
  }: {
    companyId: string;
    details: string;
    reason: string;
    reviewId: string;
  }): Promise<ReviewFlagPayloadFromGraph | undefined> => {
    const variables = {
      input: {
        companyId,
        reviewId,
        reason,
        details,
      },
    };
    const response = await client.mutate<{
      postCompanyProfileReviewFlag: ReviewFlagPayloadFromGraph;
    }>({
      mutation: POST_COMPANY_REVIEW_FLAG_MUTATE,
      variables,
    });

    if (response?.errors) {
      logger.error(
        { error: response.errors, variables },
        'Error calling postFlagReview',
      );
    }

    return response?.data?.postCompanyProfileReviewFlag;
  };

  const getGroupedCompanies = async ({
    groupBy,
    perPage = 10,
    sortBy,
    excludedCompanyIds = [],
    randomSeed,
  }: {
    groupBy: {
      group: 'INDUSTRY';
      value: string[];
    };
    excludedCompanyIds?: string[];
    perPage?: number;
    randomSeed?: string;
    sortBy?: 'REVIEWS_COUNT';
  }): Promise<CompaniesListModel> => {
    const variables = {
      groupBy,
      perPage,
      sortBy,
      zone: config.zone,
      excludedCompanyIds,
      randomSeed,
    };
    try {
      const response = await client.query<{
        groupedCompanies: GroupedCompanies[];
      }>({
        query: GET_GROUPED_COMPANIES_QUERY,
        variables,
      });

      if (response?.error || response?.errors) {
        logger.error(
          { error: response.error ?? response.errors, variables },
          'Error calling getGroupedCompanies',
        );
        return [];
      }

      return mapCompanyGroupsToFlatCompanies(response?.data?.groupedCompanies);
    } catch (error) {
      logger.error({ error, variables }, 'Error from getGroupedCompanies');
      return [];
    }
  };

  const getCompanyProfilesByName = async (
    name: string,
    includeBranding = false,
  ): Promise<SearchCompanyProfileResult> => {
    if (!name) {
      return [];
    }
    if (
      name.length < MINIMUM_CHARACTERS_SEARCH ||
      name.length > MAXIMUM_CHARACTERS_SEARCH
    ) {
      return [];
    }
    const variables = {
      name,
      includeBranding,
    };
    try {
      const response = await client.query<{
        searchCompanyProfilesByName: SearchCompanyProfileApiResult;
      }>({
        query: GET_COMPANY_PROFILES_BY_NAME_QUERY,
        variables,
        fetchPolicy: 'no-cache',
      });

      if (response?.error || response?.errors) {
        logger.error(
          { error: response.error ?? response.errors, variables },
          'Error calling getCompanyProfilesByName',
        );
      }
      return mapSearchCompanyApiResultToSearchCompanyResult(
        response?.data?.searchCompanyProfilesByName,
      );
    } catch (error) {
      logger.error({ error, variables }, 'Error from getCompanyProfilesByName');
      return [];
    }
  };

  const searchCompanyProfile = async (options: {
    name: string;
    zone: Zone;
  }): Promise<SearchResults> => {
    const { name, zone } = options;

    const defaultResponse: SearchResults = {
      data: [],
      paging: {
        page: 1,
        perPage: 0,
        total: 0,
        totalPages: 0,
      },
    };

    if (!name) {
      return defaultResponse;
    }
    if (
      name.length < MINIMUM_CHARACTERS_SEARCH ||
      name.length > MAXIMUM_CHARACTERS_SEARCH
    ) {
      return defaultResponse;
    }
    try {
      const response = await client.query<{
        searchCompanyDetails: SearchCompanyDetails;
      }>({
        query: GET_SEARCH_COMPANY_PROFILES_QUERY,
        variables: {
          filter: {
            name,
            zone,
          },
          paging: {
            pageSize: 6,
          },
        },
        fetchPolicy: 'no-cache',
      });

      const searchResults: SearchResults = {
        data:
          response?.data?.searchCompanyDetails?.data
            ?.map((item) => {
              if (!item || !item.companySlug) {
                return undefined;
              }
              const dataItem: SearchResultItem = {
                companyId: item.companyId,
                logoUrl: item.logoUrl ?? DEFAULT_LOGO_URL,
                organisationId: item.organisationId ?? undefined,
                name: item.name,
                slug: item.companySlug,
                zone,
                overallRating: {
                  rating: item.overallRating?.rating ?? undefined,
                  count: item.overallRating?.count ?? undefined,
                },
              };
              return dataItem;
            })
            .filter((item) => item !== undefined) ?? [],
        paging: {
          page: response?.data?.searchCompanyDetails?.paging?.page ?? 1,
          perPage: response?.data?.searchCompanyDetails?.paging?.perPage ?? 0,
          total: response?.data?.searchCompanyDetails?.paging?.total ?? 0,
          totalPages:
            response?.data?.searchCompanyDetails?.paging?.totalPages ?? 0,
        },
      };

      return searchResults;
    } catch (error) {
      logger.error({ error }, 'Error from searchCompanyProfile');
      return defaultResponse;
    }
  };

  return {
    searchCompanyProfile,
    getCompanyNameSlug,
    getJobs,
    getReviews,
    getUpvoteIds,
    postUpvoteReview,
    postFlagReview,
    getGroupedCompanies,
    getCompanyProfilesByName,
  };
};
